import { sum } from '@mybooks/ledger';
import { api, unwrap, type Schemas } from '../api/client';
import { useApiQuery, useCurrency } from '../api/hooks';
import { daysBetween, fmtDate, fmtMoney, today } from '../lib/format';
import { currencyOf, openBase } from '../lib/fx';
import { errorText, Icon, PopItem, Popover, usePopover } from '../ui/kit';
import type { IconName } from '../ui/icons';
import { getRec, useBankAccounts, type BankAccount, type RecDetail } from '../pages/banking/data';
import { useFeeds, type FeedConnection } from '../pages/banking/feeds';

// The prototype's rail bell (notifications()): what needs attention, built only from endpoints the
// screens already use. The queries sit under the pages' keys ('invoices', 'bills', 'bank'), so the
// invalidations those screens make after a payment, a match or a reconciliation refresh the badge.

type Doc = Schemas['Document'];
export type Notice = { key: string; icon: IconName; title: string; sub: string; to: string };

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** Every overdue invoice or bill, paging until a short page so the count and amount are exact. */
async function overdue(kind: 'invoice' | 'bill'): Promise<Doc[]> {
  const path = (kind === 'invoice' ? '/v1/invoices' : '/v1/bills') as '/v1/invoices';
  const out: Doc[] = [];
  for (let offset = 0; ; offset += 200) {
    const rows = ((await unwrap(api.GET(path, { params: { query: { overdue: 'true', limit: 200, offset } } }))).data ?? []) as Doc[];
    out.push(...rows);
    if (rows.length < 200) return out;
  }
}

/** The bell's items, in the prototype's order. Pure, so it can be tested without a server. */
export function buildNotices(input: {
  invoices: Doc[]; bills: Doc[]; accounts: BankAccount[]; recs: RecDetail[]; currency: string; asOf: string;
  feeds?: Pick<FeedConnection, 'id' | 'label' | 'status' | 'reason'>[];
}): Notice[] {
  const { invoices, bills, accounts, recs, currency, asOf, feeds = [] } = input;
  const fmt = (v: bigint) => fmtMoney(v, currency);
  const out: Notice[] = [];
  if (invoices.length) {
    const due = sum(invoices.map(openBase));
    const oldest = Math.max(...invoices.map((d) => (d.due_date ? daysBetween(d.due_date, asOf) : 0)));
    out.push({
      key: 'invoices', icon: 'doc', to: '/invoices?filter=overdue',
      title: `${invoices.length} ${plural(invoices.length, 'invoice is', 'invoices are')} overdue`,
      sub: `${fmt(due)} past due · oldest ${oldest} ${plural(oldest, 'day', 'days')}`,
    });
  }
  // A bank feed that stopped: it brings nothing in until someone acts.
  for (const f of feeds) {
    if (f.status === 'needs_attention') out.push({ key: `feed-${f.id}`, icon: 'alert', to: `/banking?feed=${f.id}`, title: `${f.label} bank feed needs attention`, sub: f.reason ?? 'Replace the token to start syncing again' });
  }
  for (const a of accounts) {
    const n = Number(a.unmatched_count);
    if (n > 0) out.push({ key: `bank-${a.id}`, icon: 'bank', to: `/banking?account=${a.id}`, title: `${n} bank ${plural(n, 'transaction', 'transactions')} to review`, sub: `${a.name} · account ${a.code}` });
  }
  if (bills.length) {
    const shown = bills.slice(0, 3).map((b) => `${b.contact_name ?? ''} · ${fmtMoney(b.balance_due, currencyOf(b, currency))}`);
    const more = bills.length > 3 ? `, and ${bills.length - 3} more` : '';
    out.push({ key: 'bills', icon: 'bill', to: '/bills?filter=overdue', title: `${bills.length} ${plural(bills.length, 'bill is', 'bills are')} overdue`, sub: shown.join(', ') + more });
  }
  for (const r of recs) {
    if (r.status !== 'open') continue;
    const month = MONTHS[Number(r.statement_date.slice(5, 7)) - 1] ?? 'A statement';
    out.push({ key: `rec-${r.id}`, icon: 'scale', to: `/reconcile?account=${r.account_id}`, title: `${month} is not reconciled`, sub: `${r.account_name} · statement ending ${fmtDate(r.statement_date)}` });
  }
  return out;
}

export function useNotices() {
  const currency = useCurrency();
  const invoices = useApiQuery(['invoices', 'overdue'], () => overdue('invoice'));
  const bills = useApiQuery(['bills', 'overdue'], () => overdue('bill'));
  const accounts = useBankAccounts();
  // The feed is optional: an environment without it answers 503, which is not a problem to show.
  const feeds = useFeeds();
  const openIds = (accounts.data ?? []).map((a) => a.open_reconciliation_id).filter((id): id is string => !!id);
  const recs = useApiQuery(['bank', 'open-recs', ...openIds], () => Promise.all(openIds.map(getRec)), { enabled: !!accounts.data });
  const all = [invoices, bills, accounts, recs];
  const loading = all.some((q) => q.isLoading);
  const error = all.find((q) => q.isError)?.error;
  const notices = loading ? [] : buildNotices({ invoices: invoices.data ?? [], bills: bills.data ?? [], accounts: accounts.data ?? [], recs: recs.data ?? [], feeds: feeds.data ?? [], currency, asOf: today() });
  return { notices, loading, error };
}

const BELL = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.9 1.9 0 0 0 3.4 0" /></svg>;

/** The rail button with its badge, and the "Needs attention" menu. */
export function NotificationsButton() {
  const pop = usePopover();
  const { notices, loading, error } = useNotices();
  const n = notices.length;
  return (
    <>
      <button className="rail-btn" onClick={pop.toggle} data-tip="Notifications" aria-label={n ? `Notifications, ${n} ${plural(n, 'needs', 'need')} attention` : 'Notifications'} aria-haspopup="true" aria-expanded="false">
        {BELL}
        {n > 0 && <span className="rail-badge" aria-hidden="true">{n}</span>}
      </button>
      {pop.anchor && (
        <Popover anchor={pop.anchor} onClose={pop.close} label="Needs attention">
          <div className="pop-title">Needs attention</div>
          {notices.map((x) => <PopItem key={x.key} icon={x.icon} title={x.title} sub={x.sub} to={x.to} />)}
          {loading ? <div className="pop-empty">Checking your books…</div>
            : error ? <div className="pop-empty" role="alert"><Icon name="alert" size={14} /> Some checks did not load: {errorText(error)}</div>
              : n === 0 && <div className="pop-empty">You are all caught up.</div>}
        </Popover>
      )}
    </>
  );
}
